import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import ConsentDialog from '../components/ConsentDialog';
import Navbar from '../components/Navbar';
import CoursesView from '../components/courses/CoursesView';
import { buildOutline, givenName } from '../lib/courseOutline';

const OUTLINE_SELECT =
  'id, title, order_index, lessons(id, title, order_index, published), quizzes(id)';

const CoursesPage = ({ session, access, profile }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(new Map());
  const [modulesByCourse, setModulesByCourse] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const loadCourses = async () => {
      const { data } = await supabase
        .from('courses')
        .select('*, lessons(count)')
        .eq('is_published', true)
        .order('order_index');

      const visibleCourses = (data || []).filter(course => {
        if (course.required_tier === 'og') {
          // Podia legacy kurzus: csak OG-nak (profiles.is_og), a PRO-nak sem —
          // ugyanez a feltétel a lessons_select és a courses_select policyben.
          return access?.is_og === true;
        }
        return true;
      });
      setCourses(visibleCourses);
      setLoading(false);
    };
    loadCourses();
  }, [access]);

  // All of the user's progress rows in one request; per-course numbers are
  // derived from it together with each course's outline.
  useEffect(() => {
    if (!session?.user?.id) return;
    const loadProgress = async () => {
      const { data } = await supabase
        .from('user_progress')
        .select('lesson_id, completed_at, updated_at')
        .eq('user_id', session.user.id);
      setProgress(new Map((data || []).map(r => [r.lesson_id, r])));
    };
    loadProgress();
  }, [session]);

  /* Plusz 2. kör (SZINTMATRIX): a Kripto Start mindenkinek látszik, a leckéi
     modulonként nyílnak (RLS + course_outline); a „paid” kurzusok Plusz-szintűek
     (Plusz, PRO), a megvásárolt kurzus szinttől függetlenül nyitva. */
  const plusOrPro = ['plusz', 'pro'].includes(access?.content_level || access?.tier);
  const canAccess = (course) => {
    if (!access) return false;
    if (course.required_tier === 'pro') return access.has_vip_tananyag;
    if (course.required_tier === 'affiliate') return true;
    if (course.required_tier === 'og') return access.is_og;
    if (course.required_tier === 'plusz' || course.required_tier === 'paid') {
      return plusOrPro || (access.purchased_course_ids || []).includes(course.id);
    }
    return false;
  };

  const accessibleCourses = courses.filter(c => canAccess(c));
  // A Kripto Start már nem zárt kurzus (a zárt modulok a kurzusoldalon látszanak).
  const lockedCourse = null;
  const outlineTargets = lockedCourse ? [...accessibleCourses, lockedCourse] : accessibleCourses;
  const outlineKey = outlineTargets.map(c => c.id).join(',');

  useEffect(() => {
    if (!outlineKey) return;
    const loadOutlines = async () => {
      const ids = outlineKey.split(',');
      const results = await Promise.all(ids.map(id =>
        supabase.from('modules').select(OUTLINE_SELECT).eq('course_id', id).order('order_index')
      ));
      const next = {};
      ids.forEach((id, i) => { next[id] = results[i].data || []; });
      setModulesByCourse(next);
    };
    loadOutlines();
  }, [outlineKey]);

  const withOutline = accessibleCourses
    .filter(c => modulesByCourse[c.id])
    .map(c => {
      const outline = buildOutline(modulesByCourse[c.id], progress);
      const lastAt = outline.lessons.reduce((max, l) => {
        const at = progress.get(l.id)?.updated_at || '';
        return at > max ? at : max;
      }, '');
      return { course: c, outline, lastAt };
    })
    // The course worked on most recently leads; untouched ones keep catalogue order.
    .sort((a, b) => (b.lastAt > a.lastAt ? 1 : b.lastAt < a.lastAt ? -1 : 0));
  const featured = withOutline[0] || null;
  const others = withOutline.slice(1);
  const locked = lockedCourse && modulesByCourse[lockedCourse.id]
    ? { course: lockedCourse, outline: buildOutline(modulesByCourse[lockedCourse.id], progress, { keepEmptyModules: true }) }
    : null;

  // Vásárlás: előbb az elállási pipa (ConsentDialog), utána a Stripe (consent: true).
  const [purchase, setPurchase] = useState(null); // { slug, bundle } | null
  const [purchaseBusy, setPurchaseBusy] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const handlePurchase = (courseSlug, isBundle = false) => {
    setPurchaseError('');
    setPurchase({ slug: courseSlug, bundle: isBundle });
  };
  const confirmPurchase = async () => {
    if (!purchase) return;
    setPurchaseBusy(true);
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const res = await fetch('https://ggeeqexormqnazwxzbsy.supabase.co/functions/v1/create-course-checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdnZWVxZXhvcm1xbmF6d3h6YnN5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzk1OTk0ODAsImV4cCI6MjA5NTE3NTQ4MH0.9yLlP94KArsxw8qNqLsLsrErG03djcqJkJPEqtUhOl0',
      },
      body: JSON.stringify({ ...(purchase.bundle ? { is_bundle: true } : { course_slug: purchase.slug }), consent: true }),
    }).catch(() => null);

    const data = res ? await res.json().catch(() => ({})) : {};
    if (data.url) {
      window.location.href = data.url;
    } else {
      setPurchaseBusy(false);
      setPurchaseError('Hiba történt a fizetés indításakor. Próbáld újra pár perc múlva.');
    }
  };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const { data } = await supabase
      .from('lessons')
      .select('id, title, duration_minutes, thumbnail_url, module_id, modules(title, courses(title, slug))')
      .ilike('title', `%${q}%`)
      .eq('published', true)
      .limit(12);
    setSearchResults(data || []);
    setSearching(false);
  };

  // Tier flags (robust to the exact tier string)
  const isPro = !!(access && (access.tier === 'pro' || access.has_vip_tananyag));
  const isPlusz = !!(access && !isPro && access.tier === 'plusz');
  const isTag = !!(access && !isPro && !isPlusz && access.has_tanfolyam);
  const tier = isPro ? 'pro' : isPlusz ? 'plusz' : isTag ? 'tag' : 'free';
  // A Kripto Start olvasható leckéi (RLS): a régi TAG-tagnál mind (134), az újnál 86.
  const ksReadable = withOutline.find(x => x.course.required_tier === 'affiliate')?.outline.stats.lessons ?? null;

  const purchasableCourses = courses.filter(c => c.required_tier === 'paid' && !canAccess(c));
  const proLessonCount = courses
    .filter(c => c.required_tier === 'pro')
    .reduce((sum, c) => sum + (c.lessons?.[0]?.count || 0), 0);
  // `courses` only holds published courses, so this is true only once Kripto 3.0 is live.
  const showOg = access?.is_og === true && courses.some(c => c.required_tier === 'og');

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117' }}>
      <Navbar access={access} />
      <CoursesView
        loading={loading || !access}
        tier={tier}
        ksReadable={ksReadable}
        session={session}
        name={givenName(profile?.full_name)}
        showOg={showOg}
        access={access}
        discordOsszekotve={Boolean(profile?.discord_user_id)}
        featured={featured}
        others={others}
        locked={locked}
        purchasable={purchasableCourses}
        proLessonCount={proLessonCount}
        search={{
          query: searchQuery,
          results: searchResults,
          searching,
          onChange: handleSearch,
          onPick: (id) => { navigate(`/lecke/${id}`); setSearchQuery(''); setSearchResults([]); },
        }}
        onStart={(lessonId) => navigate(`/lecke/${lessonId}`)}
        onOverview={(slug) => navigate(`/tanfolyam/${slug}`)}
        onPurchase={handlePurchase}
      />
      <ConsentDialog
        open={Boolean(purchase)}
        summary={purchase ? (() => {
          // A csomag sora árral (a szám és a € között nem törő szóköz).
          if (purchase.bundle) return 'Kurzuscsomag, egyszeri díj.';
          const c = courses.find(x => x.slug === purchase.slug);
          return `${c?.title || 'Kurzus'}, egyszeri díj${c?.price ? `: ${c.price}\u00a0€` : '.'}`;
        })() : null}
        loading={purchaseBusy}
        error={purchaseError}
        onConfirm={confirmPurchase}
        onCancel={() => { setPurchase(null); setPurchaseBusy(false); setPurchaseError(''); }}
      />
    </div>
  );
};

export default CoursesPage;
